import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

export interface ApiKey {
  id: string
  name: string
  key_prefix: string
  scopes: string[] | null
  is_active: boolean
  is_expired: boolean
  expires_at: string | null
  created_at: string
  last_used_at: string | null
}

export interface ApiKeyCreated extends ApiKey {
  key: string
}

interface CreateApiKeyRequest {
  name: string
  scopes?: string[]
  expires_days?: number | null
}

export function useApiKeys(tenantId: string) {
  return useQuery<ApiKey[]>({
    queryKey: ['api-keys', tenantId],
    queryFn: async () => {
      const res = await api.get(`/tenants/${tenantId}/api-keys`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

export function useCreateApiKey(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation<ApiKeyCreated, Error, CreateApiKeyRequest>({
    mutationFn: async (data) => {
      const res = await api.post(`/tenants/${tenantId}/api-keys`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', tenantId] })
    },
  })
}

export function useRevokeApiKey(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (keyId) => {
      await api.delete(`/tenants/${tenantId}/api-keys/${keyId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', tenantId] })
    },
  })
}
